import PropTypes from "prop-types";
import theme from "assets/theme";
import React, { useContext, useMemo } from "react";
import { ScrollView, StyleSheet, Text, View } from "react-native";
import RawCustomModal from "webComponentsFormElements/RawCustomModal.component";
import CustomButton from "webComponentsFormElements/inputs/CustomButton.component";
import { OrganizationContext } from "webContext/Organization.context";

const classes = StyleSheet.create({
  modal: {
    maxWidth: "75%",
    maxHeight: "95%",
    backgroundColor: theme.color.background,
    borderRadius: 10,
    alignItems: "center",
    boxShadow: "0px 2px 4px rgba(0,0,0,0.25)",
    overflow: "hidden",
  },
  headerText: {
    color: theme.color.white,
    fontSize: theme.font.size.body,
    fontWeight: theme.font.weight.semibold,
  },
  content: {
    paddingHorizontal: 40,
    paddingVertical: 20,
    zIndex: 999,
  },
  footer: {
    paddingHorizontal: 20,
    paddingBottom: 20,
    flexDirection: "row",
    gap: 100,
  },
});

function createButton(button, index) {
  return (
    <CustomButton
      key={index}
      title={button.title}
      icon={button.icon}
      variant={button.variant}
      onPress={button.onPress}
      disabled={button.disabled}
    />
  );
}

export default function CustomModal(props) {
  const {
    children,
    open,
    title,
    canSave,
    saveTitle,
    onClose,
    onSave,
    additionalButtons,
    scroll,
    isSaving = false,
  } = props;

  const {
    before: beforeButtons,
    between: betweenButtons,
    after: afterButtons,
  } = { before: [], between: [], after: [], ...additionalButtons };

  const { organizationColor } = useContext(OrganizationContext);

  const stateClasses = useMemo(
    () =>
      StyleSheet.create({
        header: {
          backgroundColor: organizationColor,
          borderTopLeftRadius: 10,
          borderTopRightRadius: 10,
          width: "100%",
          padding: 10,
        },
        modal: {
          maxWidth: "75%",
          maxHeight: "95%",
          backgroundColor: theme.color.background,
          borderRadius: 10,
          alignItems: "center",
          boxShadow: "0px 2px 4px rgba(0,0,0,0.25)",
        },
      }),
    [organizationColor],
  );

  return (
    <RawCustomModal visible={open} onClose={onClose} style={classes.modal}>
      <View style={stateClasses.header}>
        <Text style={classes.headerText}>{title}</Text>
      </View>
      {scroll ? (
        <ScrollView style={classes.content}>{children}</ScrollView>
      ) : (
        <View style={classes.content}>{children}</View>
      )}
      <View style={classes.footer}>
        {beforeButtons.map(createButton)}
        <CustomButton
          title="Close"
          icon="close-circle-outline"
          variant="cancel"
          onPress={onClose}
        />
        {betweenButtons.map(createButton)}
        {onSave && (
          <CustomButton
            title={saveTitle ?? "Save"}
            icon="content-save-outline"
            variant="dark"
            disabled={!canSave || isSaving}
            isLoading={isSaving}
            onPress={onSave}
          />
        )}
        {afterButtons.map(createButton)}
      </View>
    </RawCustomModal>
  );
}
CustomModal.propTypes = {
  children: PropTypes.node.isRequired,
  open: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  canSave: PropTypes.bool,
  saveTitle: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  additionalButtons: PropTypes.shape({
    before: PropTypes.arrayOf(PropTypes.object),
    between: PropTypes.arrayOf(PropTypes.object),
    after: PropTypes.arrayOf(PropTypes.object),
  }),
  scroll: PropTypes.bool,
  isSaving: PropTypes.bool,
};
